/**
 * Application shell.
 *
 *   #/          the demo page; a white screen and a conversation, which is all
 *               a first-time reader should have to deal with;
 *   #/run       the recorded run, case by case;
 *   #/story     the full narrative and the architecture;
 *   #/demo/...  the evidence surface for the shipped fixtures.
 *
 * Hash routing keeps the build servable from any static host with no rewrite
 * rules, and a route can never select an outcome.
 *
 * A link may carry a section after a second hash, as in `#/story#how`. The
 * browser cannot follow that on its own: it reads the whole fragment as
 * `/story#how`, matches no element, and silently stays where it is. So the
 * jump is made here instead.
 */

import { useEffect, useState } from 'react';

import { DemoPage } from './demo/DemoPage';
import { RunSurface } from './run/RunSurface';
import { NarrativePage } from './site/NarrativePage';
import { Workbench } from './workbench/Workbench';

interface Location {
  route: string;
  section: string;
}

export function parse(hash: string): Location {
  const [path, section = ''] = hash.replace(/^#\/?/, '').split('#');
  return {
    route: path.split('/').filter(Boolean)[0] ?? '',
    section,
  };
}

function read(): Location {
  return typeof window === 'undefined' ? { route: '', section: '' } : parse(window.location.hash);
}

export function App() {
  const [{ route, section }, setLocation] = useState(read);

  useEffect(() => {
    const onHash = () => setLocation(read());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // The page below has already rendered by the time this runs, so the section
  // is there to be found. Without a section, a new route opens at its top
  // rather than inheriting the last page's scroll position.
  useEffect(() => {
    if (!section) {
      window.scrollTo(0, 0);
      return;
    }
    document.getElementById(section)?.scrollIntoView({ block: 'start' });
  }, [route, section]);

  if (route === 'demo') {
    return <Workbench />;
  }
  if (route === 'run') {
    return <RunSurface />;
  }
  if (route === 'story') {
    return <NarrativePage />;
  }
  return <DemoPage />;
}
